<template>
  <div class="card series-archives-page">
    <div class="card-body">
      <div>
        <div class="page-header d-flex justify-content-between align-items-center mb-4">
          <h3 class="mb-0">
            Все архивы
          </h3>
          <b-button
            variant="primary"
            @click="showCreateModal = true"
          >
            <i class="fas fa-plus" />
            <span class="ml-1">Создать архив</span>
          </b-button>
        </div>

        <div
          v-if="!allArchives.length && !loading"
          class="text-muted mt-4"
        >
          Нет доступных архивов
        </div>

        <div
          v-else-if="loading"
          class="text-center py-5"
        >
          <b-spinner />
        </div>

        <div
          v-else
          class="row"
        >
          <div
            v-for="archive in allArchives"
            :key="archive.id"
            class="col-md-6 mb-4"
          >
            <div class="card h-100 shadow-sm border entity-card">
              <div class="card-header">
                <div class="entity-header-row mb-2 d-flex justify-content-between w-100 align-items-center">
                  <div>
                    <b-badge
                      variant="info"
                      class="mr-2"
                    >
                      Кол-во стартов: {{ (archive.starts || []).length }}
                    </b-badge>
                    <b-badge
                      variant="warning"
                      class="mr-2"
                    >
                      Средняя оценка: {{ formatReviewScore(archive.averageReviewScoreOfAllStarts) }}
                    </b-badge>
                  </div>
                  <b-button
                    size="sm"
                    variant="danger"
                    @click="removeArchive(archive)"
                  >
                    <i class="fas fa-trash-alt" />
                    <span>Удалить архив</span>
                  </b-button>
                </div>
                <span>Название архива</span>
                <input
                  v-model="archive.name"
                  class="form-control form-control-sm"
                  placeholder="Название архива"
                  @blur="updateArchive(archive)"
                >
                <span class="mt-2 d-block">Описание</span>
                <textarea
                  v-model="archive.description"
                  class="form-control form-control-sm mt-1"
                  rows="2"
                  placeholder="Описание архива"
                  @blur="updateArchive(archive)"
                />
              </div>

              <div class="card-body entity-body">
                <div class="mb-2 d-flex align-items-center">
                  <b-form-input
                    v-model="newStartIds[archive.id]"
                    type="number"
                    placeholder="ID старта"
                    size="sm"
                    class="mr-2"
                    style="max-width: 120px;"
                    @keyup.enter="addStart(archive)"
                  />
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="addStart(archive)"
                  >
                    <i class="fas fa-plus" /> Добавить
                  </b-button>
                </div>

                <div
                  v-if="!archive.starts || !archive.starts.length"
                  class="empty-state"
                >
                  Нет стартов в архиве
                </div>

                <transition-group
                  v-else
                  name="fade"
                  tag="div"
                  class="row"
                >
                  <div
                    v-for="start in archive.starts"
                    :key="start.id"
                    class="col-md-6 mb-3 item-card"
                  >
                    <div class="card h-100 shadow-sm linked-item-card">
                      <div class="card-body text-center">
                        <h6 class="card-title mb-2">
                          {{ start.name || 'Без названия' }}
                        </h6>
                        <p class="card-text text-muted mb-2">
                          ID: {{ start.id }}
                        </p>
                        <b-button
                          size="sm"
                          variant="danger"
                          @click="removeStart(archive, start)"
                        >
                          <i class="fas fa-trash-alt" /> Удалить
                        </b-button>
                      </div>
                    </div>
                  </div>
                </transition-group>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно создания архива -->
    <b-modal
      v-model="showCreateModal"
      title="Создать архив"
      hide-footer
      centered
      @hidden="resetCreateForm"
    >
      <b-form @submit.prevent="createArchive">
        <b-form-group
          label="Название архива"
          label-for="archive-name"
        >
          <b-form-input
            id="archive-name"
            v-model.trim="createForm.name"
            required
            placeholder="Введите название"
          />
        </b-form-group>

        <b-form-group
          label="Описание"
          label-for="archive-description"
        >
          <b-form-textarea
            id="archive-description"
            v-model.trim="createForm.description"
            rows="3"
            placeholder="Описание архива"
          />
        </b-form-group>

        <div class="text-right mt-4">
          <b-button
            variant="secondary"
            @click="showCreateModal = false"
          >
            Отмена
          </b-button>
          <b-button
            variant="primary"
            class="ml-2"
            type="submit"
            :disabled="createLoading"
          >
            <b-spinner
              v-if="createLoading"
              small
              class="mr-1"
            />
            Создать
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { BButton, BBadge, BModal, BForm, BFormGroup, BFormInput, BFormTextarea, BSpinner } from 'bootstrap-vue'

export default {
  name: 'AllArchivesPage',
  components: {
    BButton,
    BBadge,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BSpinner,
  },
  data() {
    return {
      allArchives: [],
      loading: false,
      showCreateModal: false,
      createForm: {
        name: '',
        description: '',
      },
      createLoading: false,
      newStartIds: {},
    }
  },
  mounted() {
    this.fetchAllArchives()
  },
  methods: {
    async fetchAllArchives() {
      this.loading = true
      try {
        const response = await this.$http.get('start/start-archive/all-archives')
        this.allArchives = response.data
      } catch (error) {
        console.error('Ошибка при загрузке архивов:', error)
        this.$bvToast.toast('Ошибка при загрузке архивов', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    formatReviewScore(score) {
      if (score == null || Number.isNaN(Number(score))) {
        return '—'
      }
      return Number(score).toFixed(1)
    },

    getArchiveStartIds(archive) {
      const starts = archive.starts || []
      return starts.map(s => s.id)
    },

    async addStart(archive) {
      const id = parseInt(this.newStartIds[archive.id], 10)
      if (!id || Number.isNaN(id)) {
        this.$bvToast.toast('Введите корректный ID старта', {
          title: 'Ошибка',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const startIds = this.getArchiveStartIds(archive)
      if (startIds.includes(id)) {
        this.$bvToast.toast('Старт уже в архиве', {
          title: 'Внимание',
          variant: 'warning',
          solid: true,
        })
        return
      }

      try {
        const payload = {
          name: archive.name,
          description: archive.description || '',
          startIds: [...startIds, id],
        }

        await this.$http.put('start/start-archive/change-archive', payload, {
          params: { id: archive.id },
        })

        await this.fetchAllArchives()

        this.$set(this.newStartIds, archive.id, '')

        this.$bvToast.toast('Старт добавлен в архив', {
          title: 'Успешно',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error(error)
        this.$bvToast.toast('Ошибка при добавлении старта', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async removeStart(archive, start) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Вы уверены, что хотите удалить старт «${start.name}» из архива "${archive.name}"?`,
        {
          title: 'Подтверждение удаления',
          okVariant: 'danger',
          okTitle: 'Удалить',
          cancelTitle: 'Отмена',
          footerClass: 'p-2',
          centered: true,
        },
      )
      if (!confirmed) return

      try {
        const startIds = this.getArchiveStartIds(archive).filter(id => id !== start.id)
        const payload = {
          name: archive.name,
          description: archive.description || '',
          startIds,
        }

        await this.$http.put('start/start-archive/change-archive', payload, {
          params: { id: archive.id },
        })

        archive.starts = (archive.starts || []).filter(s => s.id !== start.id)

        this.$bvToast.toast(`Старт «${start.name}» удалён`, {
          title: 'Успешно',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error(error)
        this.$bvToast.toast('Ошибка при удалении старта', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async updateArchive(archive) {
      try {
        const payload = {
          name: archive.name,
          description: archive.description || '',
          startIds: this.getArchiveStartIds(archive),
        }

        await this.$http.put('start/start-archive/change-archive', payload, {
          params: { id: archive.id },
        })

        this.$bvToast.toast('Архив обновлён', {
          title: 'Успешно',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error(error)
        this.$bvToast.toast('Ошибка при обновлении архива', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      }
    },

    resetCreateForm() {
      this.createForm = { name: '', description: '' }
    },

    async createArchive() {
      if (!this.createForm.name.trim()) {
        this.$bvToast.toast('Введите название архива', {
          title: 'Ошибка',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.createLoading = true
      try {
        const payload = {
          name: this.createForm.name.trim(),
          description: this.createForm.description.trim() || '',
          startIds: [],
        }

        await this.$http.post('start/start-archive/new-archive', payload)

        await this.fetchAllArchives()

        this.showCreateModal = false
        this.resetCreateForm()

        this.$bvToast.toast(`Архив "${payload.name}" успешно создан`, {
          title: 'Успешно',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Ошибка при создании архива:', error)
        this.$bvToast.toast('Ошибка при создании архива', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.createLoading = false
      }
    },

    async removeArchive(archive) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Вы уверены, что хотите удалить архив "${archive.name}"?`,
        {
          title: 'Подтверждение удаления',
          okVariant: 'danger',
          okTitle: 'Удалить',
          cancelTitle: 'Отмена',
          footerClass: 'p-2',
          centered: true,
        },
      )
      if (!confirmed) return

      try {
        await this.$http.delete('start/start-archive/remove-archive', {
          params: { id: archive.id },
        })

        this.allArchives = this.allArchives.filter(a => a.id !== archive.id)

        this.$bvToast.toast(`Архив "${archive.name}" удалён`, {
          title: 'Успешно',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error(error)
        this.$bvToast.toast('Ошибка при удалении архива', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './scss/series-archives-shared.scss';
</style>
